import { MemoryClass } from "./MemoryClass"
import type { ListItem } from "../../components/List/List"
import { AwardEntity } from "../Awards/AwardEntity"
import { EEnableStatusType } from "../../Enum/EEnableStatusType"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faBullseye, faListOl, faMoneyBill, faTag } from "@fortawesome/free-solid-svg-icons"
import i18next from "i18next"
import { EAutoType } from "../../Enum/EAutoType"
import { EMoneyConditionType } from "../../Enum/EMoneyConditionType"

export class AwardMemory extends MemoryClass<AwardEntity> {
  setList = (awardList: AwardEntity[]) => {
    this.listData = awardList
  }

  protected cloneEntity(item: AwardEntity): AwardEntity {
    return new AwardEntity({ ...item })
  }

  getListCards = (all: boolean): ListItem[] => {
    const cards: ListItem[] = []
    let count = 0

    this.listData.forEach((ss) => {
      if (all || ss.status === EEnableStatusType.Active) {
        cards.push(this.getCard(ss, count))
      }
      count++
    })

    return cards
  }

  getCard(ss: AwardEntity, count: number): ListItem {
    const enable = ss.status === EEnableStatusType.Active
    const isAutomatic = ss.isAutomatic === EAutoType.Auto
    const isPercentage = ss.conditionType === EMoneyConditionType.Percentage

    return {
      isDisable: !enable,
      id: ss.id,
      title: ss.name,
      linkImage: ss.iconUrl,
      index: count,
      dataFilter: [
        ss.name,
        ss.status.toEnableStatusTypeString(),
        isAutomatic ? "automático" : "manual",
        isPercentage ? "percentual" : "valor fixo",
      ],
      children: (
        <div
          className={`
          flex flex-col gap-3
          p-4
          rounded-2xl
          transition-all duration-300
          ${
            enable
              ? "bg-gradient-to-r from-orange-50 to-white hover:shadow-lg hover:scale-[1.01]"
              : "bg-gray-100 opacity-70"
          }
        `}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex gap-3 min-w-0 flex-1">
              <div className="w-12 h-12 rounded-xl bg-orange-200 text-orange-700 flex items-center justify-center shrink-0">
                <FontAwesomeIcon icon={faBullseye} />
              </div>

              <div className="flex flex-col justify-between min-w-0 flex-1">
                <span className="font-extrabold text-gray-900 text-lg truncate">{ss.name}</span>

                <span className="text-sm text-gray-700 line-clamp-2 font-medium">
                  {ss.description || i18next.t("components:label_no_description")}
                </span>
              </div>
            </div>

            {enable ? (
              <span className="px-3 py-1 text-xs font-bold rounded-full bg-green-100 text-green-700 whitespace-nowrap">
                Ativo
              </span>
            ) : (
              <span className="px-3 py-1 text-xs font-bold rounded-full bg-red-100 text-red-600 whitespace-nowrap">
                Inativo
              </span>
            )}
          </div>

          {ss.iconUrl && (
            <img
              src={ss.iconUrl.toClientRequest()}
              className="w-full h-28 sm:h-32 rounded-xl object-cover border border-orange-100"
            />
          )}
        </div>
      ),

      body: (
        <div className="px-4 pb-4">
          <div className="border-t border-gray-200 pt-4 flex flex-col gap-3">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2 text-gray-700 text-sm font-semibold">
                <FontAwesomeIcon icon={faMoneyBill} />
                {i18next.t("awards:label_min_consume")}
              </div>

              <span className="font-extrabold text-gray-900">{ss.minimumValue.toString().toMoney()}</span>
            </div>

            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2 text-gray-700 text-sm font-semibold">
                <FontAwesomeIcon icon={faListOl} />
                {i18next.t("awards:label_min_consume_amount")}
              </div>

              <span className="font-extrabold text-gray-900">{ss.countToWin}</span>
            </div>

            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2 text-gray-600 text-sm">
                <FontAwesomeIcon icon={faTag} />
                {i18next.t("awards:label_discount_apply")}
              </div>

              <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-700 font-bold">
                {ss.getValueToString()}
              </span>
            </div>

            <div className="flex flex-wrap gap-2 pt-1">
              <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-700 font-medium">
                {isAutomatic ? "Aplicação automática" : "Aplicação manual"}
              </span>

              <span className="px-2 py-1 text-xs rounded-full bg-indigo-100 text-indigo-700 font-medium">
                {isPercentage ? "Desconto percentual" : "Desconto fixo"}
              </span>
            </div>
          </div>
        </div>
      ),
    }
  }

  protected getItemId(item: AwardEntity): number {
    return item.id
  }
}
